<div>

  <!-- Loading -->
  @if (loading) {
    <div class="flex items-center justify-center py-12">
      <div class="h-5 w-5 animate-spin rounded-full border-b-2 border-brand-500"></div>
      <span class="ml-3 text-sm text-gray-500 dark:text-gray-400">Cargando línea de tiempo...</span>
    </div>
  }

  <!-- Error -->
  @if (error) {
    <div class="rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-500/20 dark:bg-error-500/10">
      <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  }

  <!-- Timeline -->
  @if (!loading && !error) {

    @if (entries.length === 0) {
      <div class="text-center py-10">
        <svg class="w-10 h-10 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-sm text-gray-400 dark:text-gray-500">{{ emptyMessage }}</p>
      </div>
    }

    @if (entries.length > 0) {
      <div class="relative">

        <!-- Vertical connector -->
        <div class="absolute left-5 top-5 bottom-5 w-px bg-gray-200 dark:bg-gray-700"></div>

        <div class="space-y-1">
          @for (entry of entries; track entry.id) {
            <div class="relative flex gap-4 py-3">

              <!-- Dot with operation-specific icon -->
              <div class="relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 border-white dark:border-gray-900 shadow-sm"
                [ngClass]="dotStyle(entry.visualType).bg">
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  [ngClass]="dotStyle(entry.visualType).icon">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="iconPath(entry.visualType)"/>
                </svg>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0 pt-1.5">
                <button class="w-full text-left" (click)="toggle(entry.id)">

                  <!-- Top row: badge + entity chip + chevron / date -->
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex items-center gap-1.5 flex-wrap">

                      <!-- Operation badge (UPPERCASE, colored per type) -->
                      <span class="rounded px-2 py-0.5 text-[11px] font-bold tracking-wide"
                        [ngClass]="badgeClass(entry.visualType)">
                        {{ badgeLabel(entry.visualType) }}
                      </span>

                      <!-- Entity chip (only for non-standalone events) -->
                      @if (showEntityChip(entry)) {
                        <span class="rounded px-2 py-0.5 text-[11px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {{ entityLabel(entry) }}
                        </span>
                      }

                      <!-- Expand chevron -->
                      @if (hasDiff(entry)) {
                        <svg class="h-3.5 w-3.5 text-gray-400 transition-transform duration-150"
                          [class.rotate-180]="expandedId === entry.id"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      }
                    </div>

                    <!-- Date (right side) -->
                    <span class="flex-shrink-0 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                      {{ datetime.formatDateTimeNumeric(entry.timestamp) }}
                    </span>
                  </div>

                  <!-- Headline -->
                  <p class="mt-1 text-sm text-gray-800 dark:text-gray-200">
                    {{ headline(entry) }}
                  </p>

                  <!-- Change summary (preview before expanding) -->
                  @if (changeSummary(entry); as summary) {
                    <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ summary }}</p>
                  }

                  <!-- Actor -->
                  @if (entry.userFullName || entry.username) {
                    <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                      Acción realizada por:
                      <span class="font-medium text-gray-500 dark:text-gray-400">
                        {{ entry.userFullName ?? entry.username }}
                      </span>
                    </p>
                  }

                </button>

                <!-- Changed field chips -->
                @if (changedFieldLabels(entry).length > 0) {
                  <div class="mt-2 flex flex-wrap gap-1">
                    @for (field of changedFieldLabels(entry); track field) {
                      <span class="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                        {{ field }}
                      </span>
                    }
                  </div>
                }

                <!-- Expanded diff table -->
                @if (expandedId === entry.id && hasDiff(entry)) {
                  <div class="mt-3 rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50 overflow-hidden">
                    <table class="w-full text-xs">
                      <thead>
                        <tr class="border-b border-gray-200 dark:border-gray-700 text-left">
                          <th class="px-3 py-2 font-medium text-gray-500 dark:text-gray-400">Campo</th>
                          <th class="px-3 py-2 font-medium text-red-500 dark:text-red-400">Antes</th>
                          <th class="px-3 py-2 font-medium text-green-600 dark:text-green-400">Después</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                        @for (diff of diffRows(entry); track diff.field) {
                          <tr>
                            <td class="px-3 py-1.5 font-medium text-gray-700 dark:text-gray-300" [title]="diff.field">{{ diff.label }}</td>
                            <td class="px-3 py-1.5 text-gray-500 dark:text-gray-400 break-all"
                              [class.bg-red-50]="diff.oldVal !== diff.newVal"
                              [class.dark:bg-red-500/5]="diff.oldVal !== diff.newVal">
                              {{ diff.oldVal }}
                            </td>
                            <td class="px-3 py-1.5 text-gray-700 dark:text-gray-300 break-all"
                              [class.bg-green-50]="diff.oldVal !== diff.newVal"
                              [class.dark:bg-green-500/5]="diff.oldVal !== diff.newVal">
                              {{ diff.newVal }}
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                    @if (diffRows(entry).length === 0) {
                      <div class="px-3 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">
                        No se pudieron extraer los cambios detallados.
                      </div>
                    }
                  </div>
                }

              </div>
            </div>
          }
        </div>
      </div>

      <!-- Pagination -->
      @if (totalPages > 1) {
        <div class="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
          <p class="text-xs text-gray-500 dark:text-gray-400">
            Pág. {{ currentPage + 1 }} de {{ totalPages }} ({{ totalElements }})
          </p>
          <div class="flex gap-2">
            <button
              (click)="pageChange.emit(currentPage - 1)"
              [disabled]="currentPage === 0"
              class="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
              </svg>
              Más reciente
            </button>
            <button
              (click)="pageChange.emit(currentPage + 1)"
              [disabled]="currentPage + 1 >= totalPages"
              class="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
              Más antiguo
              <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>
          </div>
        </div>
      }

    }
  }

</div>
